import $ from 'jquery';

// CONCEPT: the csrf filter (Config\Filters $globals['before']) rejects any POST/PUT/PATCH/DELETE
// without the session token. Forms carry it in a hidden field via form_open()/csrf_field(); this
// module covers the AJAX call sites without editing any of them - both the jQuery ones and, since
// DataTables 3, the ones that reach for XMLHttpRequest directly. It also covers the one case that
// is neither: a <form> assembled in JavaScript and submitted natively, which has no rendered field
// to inherit and never touches either hook - see addCsrfField() at the bottom.
// USE: importing it from main.js installs the hooks; nothing to call for an AJAX request. Only a
// form this codebase builds itself needs addCsrfField($form) before it is submitted.

function csrfToken() {
    return (window.__PAGE_CTX__ && window.__PAGE_CTX__.csrfToken) || null;
}

// The field name is Config\Security::$tokenName, published by the layouts alongside the token
// rather than written in here as 'csrf_test_name'. Renaming that setting is a one-line PHP edit
// with no visible connection to this file, and it would leave the hidden field posting under a
// key the filter no longer reads - a config value belongs on the config side of the boundary.
function csrfTokenName() {
    return (window.__PAGE_CTX__ && window.__PAGE_CTX__.csrfTokenName) || null;
}

// Never attach the token to a cross-origin call (zippopotam.us, Meilisearch): it would hand our
// token to a third party and force a CORS preflight. Relative URLs are ours by definition.
function sameOrigin(url) {
    const u = String(url || '');

    if (!/^(?:https?:)?\/\//i.test(u)) {
        return true;
    }

    return u.indexOf(window.location.origin + '/') === 0;
}

// ajaxSend rather than $.ajaxSetup: a per-call `headers` or `beforeSend` option *replaces* the
// ajaxSetup one (17 call sites pass their own beforeSend), whereas the ajaxSend event always
// fires. The token is read per request so it also works if this runs before the layout script.
$(document).ajaxSend(function (event, xhr, settings) {
    const token = csrfToken();

    if (!token || !sameOrigin(settings.url)) {
        return;
    }

    xhr.setRequestHeader('X-CSRF-TOKEN', token);
});

// DataTables 3 dropped its jQuery dependency and issues its own XMLHttpRequest, so ajaxSend above
// never fires for it - under 2.x it went through $.ajax and did, which is why the server-side
// tables started answering 403 the moment the v3 bump landed. Covering the transport rather than
// adding `ajax.headers` to each of the four server-side tables keeps this module's promise of not
// editing call sites, and catches whatever reaches for native XHR next.
const nativeOpen = XMLHttpRequest.prototype.open;
const nativeSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
const nativeSend = XMLHttpRequest.prototype.send;

XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    // send() has no access to the url, so keep it for the same-origin check there
    this.__paceRequestUrl = url;

    return nativeOpen.call(this, method, url, ...rest);
};

XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (String(name).toLowerCase() === 'x-csrf-token') {
        this.__paceCsrfSent = true;
    }

    return nativeSetRequestHeader.call(this, name, value);
};

XMLHttpRequest.prototype.send = function (body) {
    // The flag is what keeps jQuery calls correct: $.ajax uses XMLHttpRequest too, so without it
    // ajaxSend and this hook would both set the header, and XHR joins repeats of one header into
    // "token, token" rather than replacing - which the filter then rejects.
    if (!this.__paceCsrfSent && sameOrigin(this.__paceRequestUrl)) {
        const token = csrfToken();

        if (token) {
            nativeSetRequestHeader.call(this, 'X-CSRF-TOKEN', token);
        }
    }

    return nativeSend.call(this, body);
};

// Neither hook above can help a native form submit: the browser leaves the page and issues the
// POST itself, so there is no XHR to set a header on and the request body is the only place left
// to put the token. Server-rendered forms already have that covered by form_open()/csrf_field();
// a form built here (ordering/index.js assembles one for the instant-order PO) starts out with no
// field at all, which is why submitting one answered "The action you requested is not allowed."
// Issue #555.
//
// The value is csrf_hash(), the same string the header hook sends. Config\Security::$tokenRandomize
// is on, so it is the hash masked with a per-render key; Security::verify() derandomizes whatever
// it finds in $_POST before comparing, so the masked form is what it wants in both places - there
// is nothing to unmask here.
export function addCsrfField(form) {
    const token = csrfToken();
    const name = csrfTokenName();

    // A page that rendered no token has none to give. Returning empty-handed costs this one
    // submit; throwing would take out the click handler that called us, and the rest of the
    // page's JS with it.
    if (!token || !name) {
        return;
    }

    const $form = $(form);

    // A form with no action posts back to the current URL, so absent counts as ours.
    if (!sameOrigin($form.attr('action') || '')) {
        return;
    }

    // No-op if the field is already there, so this is safe to call on a form that came from
    // form_open() and on one that is submitted more than once.
    const existing = $form.find('input').filter(function () {
        return this.name === name;
    });

    if (existing.length) {
        return;
    }

    $form.append($('<input>', { type: 'hidden', name: name, value: token }));
}
